import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { Flex, HStack, IconButton, Select, Text } from "@chakra-ui/react";

import { useTasksV2Data } from "../hooks/useTasksV2Data";
import { useTasksV2Filters } from "../hooks/useTasksV2Filters";

const PAGE_SIZE_OPTIONS = [25, 50, 100];

export default function TasksV2Pagination() {
  const filters = useTasksV2Filters();
  const { rows, totalCount } = useTasksV2Data();

  const page = filters.getValue("page") ?? 1;
  const pageSize = filters.getValue("pageSize") ?? 25;

  // Total pages is based on totalCount and pageSize (approximation — see useTasksV2Data).
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const firstShown = rows.length === 0 ? 0 : (page - 1) * pageSize + 1;
  const lastShown = rows.length === 0 ? 0 : firstShown + rows.length - 1;

  function handlePrevPage() {
    if (page > 1) {
      filters.setValues({ page: page - 1 });
    }
  }

  function handleNextPage() {
    if (page < totalPages) {
      filters.setValues({ page: page + 1 });
    }
  }

  function handlePageSizeChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const newSize = parseInt(e.target.value, 10);
    filters.setValues({ pageSize: newSize, page: 1 });
  }

  return (
    <Flex
      align="center"
      borderColor="gray.100"
      borderTopWidth={1}
      gap={4}
      justify="space-between"
      px={4}
      py={3}
      wrap="wrap"
    >
      <Text color="gray.500" fontSize="sm">
        Showing{" "}
        <Text as="span" color="gray.800" fontWeight="medium">
          {firstShown}–{lastShown}
        </Text>{" "}
        of{" "}
        <Text as="span" color="gray.800" fontWeight="medium">
          {totalCount}
        </Text>{" "}
        tasks
      </Text>

      <HStack spacing={3}>
        <Select
          borderRadius="md"
          size="sm"
          value={pageSize}
          w="110px"
          onChange={handlePageSizeChange}
        >
          {PAGE_SIZE_OPTIONS.map((size) => (
            <option key={size} value={size}>
              {size} / page
            </option>
          ))}
        </Select>
        <HStack spacing={1}>
          <IconButton
            aria-label="Previous page"
            icon={<ChevronLeftIcon boxSize={5} />}
            isDisabled={page <= 1}
            size="sm"
            variant="ghost"
            onClick={handlePrevPage}
          />
          <Text color="gray.600" fontSize="sm" minW="88px" textAlign="center">
            Page {page} of {totalPages}
          </Text>
          <IconButton
            aria-label="Next page"
            icon={<ChevronRightIcon boxSize={5} />}
            isDisabled={page >= totalPages}
            size="sm"
            variant="ghost"
            onClick={handleNextPage}
          />
        </HStack>
      </HStack>
    </Flex>
  );
}
